/**
 * LayoutComponents.tsx
 * Layout components category page - Badge, Card, Chip, Divider, Drawer, Accordion
 * 
 * Showcases all layout and structural HeroUI components with organized layout
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Layers, Award, Tag, Minus, PanelLeftOpen, ChevronDown, ArrowUpDown, Columns } from 'lucide-react';

export const LayoutComponents: React.FC = () => {
  const navigate = useNavigate();

  const layoutComponents = [
    {
      name: 'Badge',
      description: 'Overlay notifications and counts for UI elements',
      status: 'available',
      heroUIBase: 'Badge',
      route: '/playground/components/layout/badge',
      icon: Award,
      preview: (
        <div className="flex items-center justify-center">
          <Badge color="primary" content="3">
            <Button variant="bordered" color="primary" size="sm">Inbox</Button>
          </Badge>
        </div>
      )
    },
    {
      name: 'Card',
      description: 'Flexible card container with variants and shadows',
      status: 'available',
      heroUIBase: 'Card',
      route: '/playground/components/layout/card',
      icon: Layers,
      preview: (
        <div className="space-y-2">
          <Card className="p-2">
            <div className="text-xs font-medium">Surface Card</div>
            <div className="text-xs text-text-secondary">With border styling</div>
          </Card>
          <div className="flex gap-2">
            <Card shadow="lg" isHoverable className="p-2 flex-1">
              <div className="text-xs text-center">Elevated</div>
            </Card>
            <Card className="p-2 flex-1">
              <div className="text-xs text-center">Background</div>
            </Card>
          </div>
        </div>
      )
    },
    {
      name: 'Chip',
      description: 'Interactive tags and status indicators',
      status: 'available',
      heroUIBase: 'Chip',
      route: '/playground/components/layout/chip',
      icon: Tag,
      preview: (
        <div className="flex gap-2 items-center justify-center">
          <Chip size="sm" color="primary">Primary</Chip>
          <Chip size="sm" color="secondary">Secondary</Chip>
        </div>
      )
    },
    {
      name: 'Divider',
      description: 'Visual content separators with text support',
      status: 'available',
      heroUIBase: 'Divider',
      route: '/playground/components/layout/divider',
      icon: Minus,
      preview: (
        <div className="text-xs space-y-2">
          <div className="text-center text-text-secondary">Content Above</div>
          <div className="h-px bg-border"></div>
          <div className="text-center text-text-secondary">Content Below</div>
          <div className="text-text-tertiary text-center">Visual Separator</div>
        </div>
      )
    },
    {
      name: 'Drawer',
      description: 'Slide-out panels for navigation and content overlay',
      status: 'available',
      heroUIBase: 'Drawer',
      route: '/playground/components/layout/drawer',
      icon: PanelLeftOpen,
      preview: (
        <div className="text-xs space-y-2">
          <div className="relative border border-border rounded h-16">
            <div className="absolute left-0 top-0 bottom-0 w-6 bg-surface border-r border-border flex items-center justify-center">
              <div className="w-2 h-8 bg-primary rounded"></div>
            </div>
            <div className="ml-8 p-2">
              <div className="text-xs text-text-secondary">Main Content</div>
            </div>
          </div>
          <div className="text-text-tertiary text-center">Slide-out Panel</div>
        </div>
      )
    },
    {
      name: 'Accordion',
      description: 'Collapsible content panels with smooth animations',
      status: 'available',
      heroUIBase: 'Accordion',
      route: '/playground/components/layout/accordion',
      icon: ChevronDown,
      preview: (
        <div className="text-xs space-y-1">
          <div className="border border-border rounded">
            <div className="flex items-center justify-between p-2 bg-surface hover:bg-hover">
              <span className="font-medium">Section 1</span>
              <ChevronDown className="w-3 h-3" />
            </div>
            <div className="p-2 text-text-secondary bg-background">
              Collapsible content
            </div>
          </div>
          <div className="text-text-tertiary text-center">Expandable Panels</div>
        </div>
      )
    },
    {
      name: 'Scroll Shadow',
      description: 'Visual scroll indicators with dynamic shadows for scrollable content',
      status: 'available',
      heroUIBase: 'ScrollShadow',
      route: '/playground/components/layout/scroll-shadow',
      icon: ArrowUpDown,
      preview: (
        <div className="text-xs space-y-2">
          <div className="w-full h-12 border border-border rounded overflow-hidden relative">
            <div className="absolute inset-x-0 top-0 h-2 bg-linear-to-b from-primary-200/50 to-transparent"></div>
            <div className="p-2 space-y-1 text-text-secondary overflow-y-auto h-full">
              <div>Content line 1</div>
              <div>Content line 2</div>
              <div>Content line 3</div>
              <div>More content...</div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-2 bg-linear-to-t from-primary-200/50 to-transparent"></div>
          </div>
          <div className="text-text-tertiary text-center">Scroll Indicators</div>
        </div>
      )
    },
    {
      name: 'Split Pane',
      description: 'Orientation-agnostic two-pane layout with drag-to-resize divider',
      status: 'available',
      heroUIBase: 'Custom',
      route: '/playground/components/layout/split-pane',
      icon: Columns,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex h-12 border border-border rounded overflow-hidden">
            <div className="w-1/3 bg-primary/10 flex items-center justify-center">
              <span className="text-primary">Left</span>
            </div>
            <div className="w-px bg-border"></div>
            <div className="flex-1 bg-secondary/10 flex items-center justify-center">
              <span className="text-secondary">Right</span>
            </div>
          </div>
          <div className="text-text-tertiary text-center">Resizable Split Layout</div>
        </div>
      )
    }
  ];

  const handleComponentClick = (route: string) => {
    navigate(route);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-6">
        
        {/* Category Header */}
        <div className="text-center px-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layers className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Layout Components</h1>
          </div>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Structural components for organizing and presenting content.
            Build beautiful layouts with cards, dividers, badges, and interactive containers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <Badge color="success" variant="flat">{layoutComponents.length} components</Badge>
            <Chip size="sm" color="primary" variant="dot">HeroUI Based</Chip>
          </div>
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4 md:px-0">
          {layoutComponents.map((component) => {
            const IconComponent = component.icon;
            
            return (
              <Card
                key={component.name}
                isPressable
                onPress={() => handleComponentClick(component.route)}
                className="group hover:shadow-lg transition-all duration-200"
              >
                <CardHeader>
                  <div className="flex items-start justify-between w-full gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <IconComponent className="w-5 h-5 text-primary shrink-0" />
                      <h3 className="font-semibold text-primary text-base md:text-lg truncate">{component.name}</h3>
                    </div>
                    <Chip 
                      color={component.status === 'available' ? 'success' : 'default'}
                      size="sm"
                      variant="flat"
                      className="shrink-0"
                    >
                      {component.status}
                    </Chip>
                  </div>
                </CardHeader>
                
                <CardBody className="space-y-4">
                  <p className="text-text-secondary">{component.description}</p>
                  
                  {/* Component Preview */}
                  <div className="p-4 bg-background rounded-lg border border-border min-h-30 flex items-center justify-center">
                    {component.preview}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Chip size="sm" variant="bordered" color="primary">
                      {component.heroUIBase}
                    </Chip>
                  </div>
                </CardBody>
                
                <CardFooter>
                  <Button 
                    variant="bordered" 
                    color="primary"
                    onPress={() => handleComponentClick(component.route)}
                    className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                  >
                    View Examples & Documentation →
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Category Info */}
        <Card className="bg-surface">
          <CardBody>
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">About Layout Components</h3>
              <p className="text-text-secondary max-w-3xl mx-auto">
                Layout components provide the structural foundation for your user interfaces. 
                They help organize content, provide visual hierarchy, and create engaging interactive experiences 
                with consistent styling and smooth animations.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Chip size="sm" variant="flat" color="primary">Flexible Layouts</Chip>
                <Chip size="sm" variant="flat" color="secondary">Smooth Animations</Chip>
                <Chip size="sm" variant="flat" color="success">Responsive Design</Chip>
                <Chip size="sm" variant="flat" color="warning">Interactive Elements</Chip>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};